<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useAdmin } from 'dashboard/composables/useAdmin';
import InboxesAPI from 'dashboard/api/inboxes';
import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import OptionsBuilder from './OptionsBuilder.vue';
import {
  MAX_BODY_TEXT,
  PRESENTATION_AUTO,
  PRESENTATION_BUTTON,
  PRESENTATION_LIST,
  buildFlowAttributes,
  buildOptionsAttributes,
  resolvePresentation,
} from './constants';

const props = defineProps({
  show: { type: Boolean, default: false },
  inboxId: { type: Number, default: undefined },
});
const emit = defineEmits(['onSend', 'cancel', 'update:show']);

const { t } = useI18n();
// Syncing flows from Meta is an admin action (InboxPolicy#sync_flows?).
const { isAdmin } = useAdmin();

const TABS = { OPTIONS: 'options', FLOW: 'flow' };

const activeTab = ref(TABS.OPTIONS);
const header = ref('');
const body = ref('');
const footer = ref('');
const presentation = ref(PRESENTATION_AUTO);
const listButtonLabel = ref('');
const options = ref([{ title: '', value: '', description: '', section: '' }]);

const flows = ref([]);
const selectedFlowId = ref('');
const flowCta = ref('');
const flowScreen = ref('');
const flowScreenData = ref('');
const showFlowAdvanced = ref(false);
const isSyncingFlows = ref(false);
const isLoadingFlows = ref(false);

const localShow = computed({
  get: () => props.show,
  set: value => emit('update:show', value),
});

const resolvedPresentation = computed(() =>
  resolvePresentation(presentation.value, options.value)
);

const filledOptions = computed(() =>
  options.value.filter(option => option.title.trim())
);

// Screen data is optional, but a half-typed JSON object should block the send
// rather than reach Meta as a string.
const parsedScreenData = computed(() => {
  if (!flowScreenData.value.trim()) return { valid: true, value: null };
  try {
    return { valid: true, value: JSON.parse(flowScreenData.value) };
  } catch (error) {
    return { valid: false, value: null };
  }
});

const optionsError = computed(() => {
  if (!body.value.trim()) return t('WHATSAPP_INTERACTIVE.FORM.BODY_REQUIRED');
  if (!filledOptions.value.length)
    return t('WHATSAPP_INTERACTIVE.FORM.OPTIONS_REQUIRED');
  if (
    resolvedPresentation.value === PRESENTATION_BUTTON &&
    filledOptions.value.length > 3
  )
    return t('WHATSAPP_INTERACTIVE.FORM.BUTTON_LIMIT');
  if (
    resolvedPresentation.value === PRESENTATION_LIST &&
    filledOptions.value.length > 10
  )
    return t('WHATSAPP_INTERACTIVE.FORM.LIST_LIMIT');
  return '';
});

const flowError = computed(() => {
  if (!body.value.trim()) return t('WHATSAPP_INTERACTIVE.FORM.BODY_REQUIRED');
  if (!selectedFlowId.value) return t('WHATSAPP_INTERACTIVE.FLOW.REQUIRED');
  if (!parsedScreenData.value.valid)
    return t('WHATSAPP_INTERACTIVE.FLOW.DATA_INVALID');
  return '';
});

const currentError = computed(() =>
  activeTab.value === TABS.OPTIONS ? optionsError.value : flowError.value
);

const fetchFlows = async () => {
  if (!props.inboxId) return;
  isLoadingFlows.value = true;
  try {
    const { data } = await InboxesAPI.getFlows(props.inboxId, {
      published: 'true',
    });
    flows.value = data?.payload || [];
  } catch (error) {
    flows.value = [];
  } finally {
    isLoadingFlows.value = false;
  }
};

const syncFlows = async () => {
  isSyncingFlows.value = true;
  try {
    await InboxesAPI.syncFlows(props.inboxId);
    useAlert(t('WHATSAPP_INTERACTIVE.FLOW.SYNC_SUCCESS'));
  } catch (error) {
    useAlert(
      error?.response?.data?.error || t('WHATSAPP_INTERACTIVE.FLOW.SYNC_ERROR')
    );
  } finally {
    isSyncingFlows.value = false;
  }
};

const reset = () => {
  activeTab.value = TABS.OPTIONS;
  header.value = '';
  body.value = '';
  footer.value = '';
  presentation.value = PRESENTATION_AUTO;
  listButtonLabel.value = '';
  options.value = [{ title: '', value: '', description: '', section: '' }];
  selectedFlowId.value = '';
  flowCta.value = '';
  flowScreen.value = '';
  flowScreenData.value = '';
  showFlowAdvanced.value = false;
};

const onClose = () => {
  emit('cancel');
};

const sendOptions = () => {
  const contentAttributes = buildOptionsAttributes({
    options: filledOptions.value,
    presentation: presentation.value,
    header: header.value,
    footer: footer.value,
    listButtonLabel: listButtonLabel.value,
  });
  // The timeline preview lists the options, which the interactive bubble itself
  // cannot show until the message round-trips.
  const preview = [
    body.value.trim(),
    ...filledOptions.value.map(option => `• ${option.title.trim()}`),
  ].join('\n');

  emit('onSend', {
    message: body.value.trim(),
    pendingMessageContent: preview,
    contentType: 'input_select',
    contentAttributes,
  });
  reset();
};

const sendFlow = () => {
  const contentAttributes = buildFlowAttributes({
    flowId: selectedFlowId.value,
    cta: flowCta.value,
    header: header.value,
    footer: footer.value,
    screen: flowScreen.value,
    screenData: parsedScreenData.value.value,
  });

  emit('onSend', {
    message: body.value.trim(),
    pendingMessageContent: body.value.trim(),
    contentType: 'whatsapp_flow',
    contentAttributes,
  });
  reset();
};

const onSubmit = () => {
  if (currentError.value) return;
  if (activeTab.value === TABS.OPTIONS) sendOptions();
  else sendFlow();
};

watch(
  () => props.show,
  isVisible => {
    if (isVisible) fetchFlows();
    else reset();
  }
);
</script>

<template>
  <woot-modal v-model:show="localShow" :on-close="onClose" size="modal-big">
    <woot-modal-header
      :header-title="t('WHATSAPP_INTERACTIVE.MODAL.TITLE')"
      :header-content="t('WHATSAPP_INTERACTIVE.MODAL.SUBTITLE')"
    />
    <div class="flex flex-col gap-5 px-8 py-6">
      <div class="flex gap-2">
        <Button
          :variant="activeTab === TABS.OPTIONS ? 'solid' : 'faded'"
          color="slate"
          size="sm"
          :label="t('WHATSAPP_INTERACTIVE.MODAL.TAB_OPTIONS')"
          @click="activeTab = TABS.OPTIONS"
        />
        <Button
          :variant="activeTab === TABS.FLOW ? 'solid' : 'faded'"
          color="slate"
          size="sm"
          :label="t('WHATSAPP_INTERACTIVE.MODAL.TAB_FLOW')"
          @click="activeTab = TABS.FLOW"
        />
      </div>

      <Input
        v-model="header"
        :label="t('WHATSAPP_INTERACTIVE.FORM.HEADER')"
        :placeholder="t('WHATSAPP_INTERACTIVE.FORM.HEADER_PLACEHOLDER')"
      />

      <div class="flex flex-col gap-1">
        <label
          class="text-sm font-medium text-n-slate-12"
          for="interactive-body"
        >
          {{ t('WHATSAPP_INTERACTIVE.FORM.BODY') }}
        </label>
        <textarea
          id="interactive-body"
          v-model="body"
          rows="3"
          :maxlength="MAX_BODY_TEXT"
          :placeholder="t('WHATSAPP_INTERACTIVE.FORM.BODY_PLACEHOLDER')"
          class="w-full px-3 py-2 text-sm rounded-lg resize-y bg-n-alpha-black2 text-n-slate-12 outline outline-1 outline-n-weak focus:outline-n-brand"
        />
      </div>

      <Input
        v-model="footer"
        :label="t('WHATSAPP_INTERACTIVE.FORM.FOOTER')"
        :placeholder="t('WHATSAPP_INTERACTIVE.FORM.FOOTER_PLACEHOLDER')"
      />

      <template v-if="activeTab === TABS.OPTIONS">
        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-n-slate-12" for="presentation">
            {{ t('WHATSAPP_INTERACTIVE.FORM.PRESENTATION') }}
          </label>
          <select
            id="presentation"
            v-model="presentation"
            class="w-full px-3 py-2 text-sm rounded-lg bg-n-alpha-black2 text-n-slate-12 outline outline-1 outline-n-weak"
          >
            <option :value="PRESENTATION_AUTO">
              {{ t('WHATSAPP_INTERACTIVE.FORM.PRESENTATION_AUTO') }}
            </option>
            <option :value="PRESENTATION_BUTTON">
              {{ t('WHATSAPP_INTERACTIVE.FORM.PRESENTATION_BUTTON') }}
            </option>
            <option :value="PRESENTATION_LIST">
              {{ t('WHATSAPP_INTERACTIVE.FORM.PRESENTATION_LIST') }}
            </option>
          </select>
          <span class="text-xs text-n-slate-11">
            {{ t('WHATSAPP_INTERACTIVE.FORM.PRESENTATION_HINT') }}
          </span>
        </div>

        <Input
          v-if="resolvedPresentation === PRESENTATION_LIST"
          v-model="listButtonLabel"
          :label="t('WHATSAPP_INTERACTIVE.FORM.LIST_BUTTON_LABEL')"
        />

        <OptionsBuilder v-model="options" :presentation="presentation" />
      </template>

      <template v-else>
        <div class="flex flex-col gap-1">
          <div class="flex items-center justify-between">
            <label
              class="text-sm font-medium text-n-slate-12"
              for="flow-select"
            >
              {{ t('WHATSAPP_INTERACTIVE.FLOW.SELECT') }}
            </label>
            <Button
              v-if="isAdmin"
              variant="link"
              color="slate"
              size="xs"
              :is-loading="isSyncingFlows"
              :label="t('WHATSAPP_INTERACTIVE.FLOW.SYNC')"
              @click="syncFlows"
            />
          </div>
          <select
            id="flow-select"
            v-model="selectedFlowId"
            :disabled="isLoadingFlows"
            class="w-full px-3 py-2 text-sm rounded-lg bg-n-alpha-black2 text-n-slate-12 outline outline-1 outline-n-weak"
          >
            <option value="">
              {{ t('WHATSAPP_INTERACTIVE.FLOW.SELECT_PLACEHOLDER') }}
            </option>
            <option v-for="flow in flows" :key="flow.id" :value="flow.id">
              {{ flow.name }}
            </option>
          </select>
          <span
            v-if="!isLoadingFlows && !flows.length"
            class="text-xs text-n-slate-11"
          >
            {{ t('WHATSAPP_INTERACTIVE.FLOW.NONE') }}
          </span>
        </div>

        <Input
          v-model="flowCta"
          :label="t('WHATSAPP_INTERACTIVE.FLOW.CTA')"
          :placeholder="t('WHATSAPP_INTERACTIVE.FLOW.CTA_PLACEHOLDER')"
        />

        <Button
          variant="link"
          color="slate"
          size="xs"
          justify="start"
          :label="t('WHATSAPP_INTERACTIVE.FLOW.ADVANCED')"
          @click="showFlowAdvanced = !showFlowAdvanced"
        />

        <template v-if="showFlowAdvanced">
          <Input
            v-model="flowScreen"
            :label="t('WHATSAPP_INTERACTIVE.FLOW.SCREEN')"
            :placeholder="t('WHATSAPP_INTERACTIVE.FLOW.SCREEN_PLACEHOLDER')"
            :message="t('WHATSAPP_INTERACTIVE.FLOW.ACTION_NAVIGATE')"
          />
          <div class="flex flex-col gap-1">
            <label class="text-sm font-medium text-n-slate-12" for="flow-data">
              {{ t('WHATSAPP_INTERACTIVE.FLOW.DATA') }}
            </label>
            <textarea
              id="flow-data"
              v-model="flowScreenData"
              rows="3"
              placeholder='{ "plan": "pro" }'
              class="w-full px-3 py-2 font-mono text-xs rounded-lg resize-y bg-n-alpha-black2 text-n-slate-12 outline outline-1 outline-n-weak focus:outline-n-brand"
            />
          </div>
        </template>
      </template>

      <div class="flex items-center justify-end gap-3 pt-2">
        <span v-if="currentError" class="mr-auto text-xs text-n-ruby-9">
          {{ currentError }}
        </span>
        <Button
          variant="faded"
          color="slate"
          :label="t('WHATSAPP_INTERACTIVE.CANCEL')"
          @click="onClose"
        />
        <Button
          color="blue"
          :disabled="!!currentError"
          :label="t('WHATSAPP_INTERACTIVE.SEND')"
          @click="onSubmit"
        />
      </div>
    </div>
  </woot-modal>
</template>
